// src/pages/Home.jsx
//
// Universal home / team picker. Replaces the role-split landing pages
// (Dashboard for coaches, /parent/teams for parents) with a single
// surface that shows EVERY team the user is on, with a role badge per
// team. Tap a team → that team's detail page; the team-scoped UI
// inside decides what actions to expose based on the user's role on
// THAT team. No global "parent mode" / "coach mode" — there is no
// mode, only context.
//
// Mobile-first: vertical stack of full-width cards. Tap targets are
// 88px+ tall, designed for thumb reach.
//
// Empty state: clear next-action CTAs split by intent ("create team"
// for coaches, "request invite" for parents-to-be).

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useNavigationType } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TodayPanel from '@/components/home/TodayPanel';
import NextStepCard from '@/components/home/NextStepCard';
import { sportLabel, genderLabel } from '@/utils/displayLabels';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import { useTeam } from '@/context/TeamContext';
import { useRole } from '@/context/RoleContext';
import {
  ChevronRightIcon,
  UsersIcon,
  PlusIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/**
 * Determine the user's role on a given team. Returns one of:
 *   'owner'  — they own / head-coach this team
 *   'staff'  — assistant / manager
 *   'parent' — parent of a player on this team
 *   'member' — fallback for any other membership
 */
function roleForTeam(team, uid) {
  if (!team || !uid) return 'member';
  if (team.ownerId === uid || team.coachId === uid) return 'owner';
  if (
    (Array.isArray(team.staffIds) && team.staffIds.includes(uid)) ||
    (Array.isArray(team.managerUids) && team.managerUids.includes(uid))
  ) {
    return 'staff';
  }
  if (Array.isArray(team.parentIds) && team.parentIds.includes(uid)) {
    return 'parent';
  }
  return 'member';
}

/** Compact role badge (mobile-friendly). */
function RoleBadge({ role }) {
  const { t } = useTranslation();
  const styles = {
    owner:  'bg-[#b5f03d]/20 text-[#3d6010] ring-1 ring-[#5a8a1f]/30',
    staff:  'bg-blue-100 text-blue-700 ring-1 ring-blue-200',
    parent: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200',
    member: 'bg-zinc-100 text-zinc-600 ring-1 ring-zinc-200',
  };
  // owner badge reads "COACH"; map roles → i18n role keys.
  const roleKey = { owner: 'coach', staff: 'staff', parent: 'parent', member: 'member' }[role] || 'member';
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.5px] ${styles[role]}`}
      style={{ fontFamily: HEAD_FONT }}
    >
      {t(`home.role.${roleKey}`)}
    </span>
  );
}

/** Single team card in the picker. */
function TeamCard({ team }) {
  const { t, i18n } = useTranslation();
  const role = team._role;
  // Sport/gender are stored English values — localized for display only.
  const meta = [
    sportLabel(team.sport || team.sportType, i18n?.language),
    team.ageGroup,
    genderLabel(team.gender, i18n?.language),
  ].filter(Boolean).join(' · ');

  return (
    <Link
      to={`/teams/${team.id}`}
      className="group flex items-center gap-4 rounded-2xl border border-zinc-200 bg-white p-4 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] transition-all hover:border-[#b5f03d]/60 hover:shadow-[0_4px_12px_-4px_rgba(13,31,20,0.12)] hover:-translate-y-0.5 active:scale-[0.99]"
    >
      {/* Logo or initial */}
      {team.logoUrl ? (
        <img
          src={team.logoUrl}
          alt=""
          className="h-14 w-14 shrink-0 rounded-xl object-cover ring-1 ring-zinc-200"
        />
      ) : (
        <div
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 text-[#3d6010]"
          style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 22 }}
        >
          {(team.name || '?').charAt(0).toUpperCase()}
        </div>
      )}

      {/* Name + meta + role */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h3
            className="truncate text-base sm:text-lg font-bold uppercase tracking-tight text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.2px' }}
          >
            {team.name || t('home.unnamedTeam')}
          </h3>
          <RoleBadge role={role} />
        </div>
        {meta && (
          <p className="mt-0.5 text-xs sm:text-sm text-zinc-500 truncate">
            {meta}
          </p>
        )}
      </div>

      <ChevronRightIcon className="h-5 w-5 shrink-0 text-zinc-300 transition-colors group-hover:text-[#5a8a1f]" />
    </Link>
  );
}

function SkeletonCard() {
  return <div className="h-20 rounded-2xl bg-zinc-100 animate-pulse" />;
}

/* Empty-state CTA card. The picker is empty for two distinct reasons:
   1. New account, no teams yet (most likely a coach who hasn't
      created their first team)
   2. A parent who hasn't accepted any invite yet
   We surface both paths so neither user type feels stuck. */
function EmptyState({ firstName }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-8 sm:p-10 text-center shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
        <UsersIcon className="h-7 w-7 text-[#5a8a1f]" />
      </div>
      <h2
        className="text-xl sm:text-2xl font-bold uppercase tracking-wide text-[#0d1f14]"
        style={{ fontFamily: HEAD_FONT }}
      >
        {firstName ? t('home.emptyTitleName', { name: firstName }) : t('home.emptyTitleNoName')}
      </h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-zinc-500 leading-relaxed">
        {t('home.emptyBody')}
      </p>
      <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
        <Link
          to="/teams/create"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          <PlusIcon className="h-4 w-4" />
          {t('home.createTeam')}
        </Link>
        <a
          href="mailto:hello@getscrimly.com?subject=Scrimly%20parent%20invite%20request"
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-[#b5f03d]/40 px-5 py-2.5 text-sm font-bold uppercase text-[#0d1f14] transition-colors"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          <EnvelopeIcon className="h-4 w-4" />
          {t('home.getHelp')}
        </a>
      </div>
    </div>
  );
}

export default function Home() {
  const { t } = useTranslation();
  const { firebaseUser, userProfile } = useAuth();
  const uid = firebaseUser?.uid;
  const { teams: coachTeams, loadingTeams } = useTeam();
  const { parentTeamIds, isParent } = useRole();

  // Hydrate parent team docs from the IDs we get out of RoleContext.
  // TeamContext currently queries ownerId / coachUid / coachId /
  // memberUids / members / managerUids — which doesn't include
  // parentIds. Until we consolidate that, we fetch the parent team
  // docs separately right here.
  const [parentTeams, setParentTeams] = useState([]);
  const [loadingParent, setLoadingParent] = useState(true);
  useEffect(() => {
    if (!uid) {
      setParentTeams([]);
      setLoadingParent(false);
      return;
    }
    if (parentTeamIds.size === 0) {
      setParentTeams([]);
      setLoadingParent(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ids = Array.from(parentTeamIds);
        const docs = await Promise.all(
          ids.map((id) => getDoc(doc(db, 'teams', id)))
        );
        if (cancelled) return;
        setParentTeams(
          docs
            .filter((d) => d.exists())
            .map((d) => ({ id: d.id, ...d.data() }))
        );
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[Home] parent team hydrate failed', err);
        }
      } finally {
        if (!cancelled) setLoadingParent(false);
      }
    })();
    return () => { cancelled = true; };
  }, [uid, parentTeamIds]);

  // Combine coach + parent teams, dedupe by id (a user can hold both
  // roles on the same team — coach of own kid). For each team, attach
  // the user's role on that team via roleForTeam.
  const allTeams = useMemo(() => {
    const merged = new Map();
    [...coachTeams, ...parentTeams].forEach((t) => {
      if (!t?.id) return;
      // Filter archived teams from the home view. Keeps the picker
      // clean; archived teams are still reachable via the My Teams
      // page's "Show archived" toggle.
      const archived =
        t.isArchived === undefined ? !!t.archived : !!t.isArchived;
      if (archived) return;
      if (!merged.has(t.id)) {
        merged.set(t.id, { ...t, _role: roleForTeam(t, uid) });
      }
    });
    return Array.from(merged.values()).sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    );
  }, [coachTeams, parentTeams, uid]);

  const loading = loadingTeams || loadingParent;
  // Prefer the editable displayName (what Profile settings + the sidebar use)
  // over a legacy firstName field that the profile editor never updates —
  // otherwise an old firstName makes the greeting ignore name changes.
  const firstName = (userProfile?.displayName || userProfile?.firstName || '')
    .split(' ')[0]
    .trim();

  // Smart bypass for single-team users: skip the dead-tap team
  // picker on initial app load (post-login or direct URL) and go
  // straight to the single team's detail page. When the user
  // explicitly clicks "Dashboard" in the sidebar, show the picker
  // anyway — that's how single-team coaches still discover the
  // "Create another team" CTA without it being buried.
  //
  // Logic:
  //   - If the picker is going to render (allTeams !== 1, or the
  //     user explicitly PUSH-navigated here), mark this session
  //     as "picker seen."
  //   - If the picker has been seen this session, never auto-bypass
  //     again — even on refresh — because the user has now used
  //     the picker as a real surface and we shouldn't yank them
  //     out of it on F5.
  //   - Only bypass when: single team, didn't get here via PUSH,
  //     and the picker has never been seen in this session.
  //
  // sessionStorage scope means the flag clears on tab close.
  const navigate = useNavigate();
  const navType = useNavigationType();
  useEffect(() => {
    if (loading) return;

    const willShowPicker = allTeams.length !== 1 || navType === 'PUSH';
    let alreadySeen = false;
    try {
      alreadySeen = sessionStorage.getItem('scrimly.dashboardSeen') === '1';
    } catch {}

    if (willShowPicker || alreadySeen) {
      try { sessionStorage.setItem('scrimly.dashboardSeen', '1'); } catch {}
      return;
    }
    // Single team, not explicit nav, picker never seen → bypass.
    navigate(`/teams/${allTeams[0].id}`, { replace: true });
  }, [loading, allTeams, navType, navigate]);

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-6 sm:py-10">
        {/* Header — keeps it light. The team cards do the work. */}
        <header className="mb-6 sm:mb-8">
          <p
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
            style={{ fontFamily: HEAD_FONT }}
          >
            Scrimly
          </p>
          <h1
            className="mt-1 text-2xl sm:text-3xl font-bold uppercase tracking-tight text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT }}
          >
            {firstName ? t('home.greetingName', { name: firstName }) : t('home.greetingNoName')}
          </h1>
          {!loading && allTeams.length > 0 && (
            <p className="mt-1 text-sm text-zinc-500">
              {t('home.teamCount', { count: allTeams.length })}
            </p>
          )}
        </header>

        {/* NEXT STEP card — single-card "what to do next" surface that
            replaces the old multi-card setup checklist on Home. Renders
            ONE prompt at a time (the most-urgent setup gap) with one
            primary CTA and a "skip for now" link that bumps the user
            to the next step in the ladder.

            Suppressed for pure parents: a user whose global signup
            role is 'parent' AND who holds no coach memberships has
            nothing to set up — the card would just be noise. Coaches
            with no teams yet still see the card (it shows step 1:
            "Create your first team"). */}
        {!loading && !(isParent && coachTeams.length === 0) && (
          <NextStepCard coachTeams={coachTeams} loading={loadingTeams} />
        )}

        {/* TODAY panel — cross-team smart-action rollup for coaches.
            Shows the most-urgent pending action per team (closing reg
            / unpaid / recap due / lineup gap / no field) so coaches
            don't have to tap into every team to see what needs
            attention.

            Renders for any coach with ≥1 team. Single-team coaches
            normally hit Smart Bypass on Home and skip this surface,
            but if they explicitly navigate here (via sidebar Home or
            logo click), they should still see their one team's
            prompts — same data they'd see in TeamSmartActions on the
            team page, presented as a Home rollup for consistency.

            Pure parents (no coach teams) skip since `coachTeams` is
            empty. Renders quietly with null rows when nothing is
            urgent across teams. */}
        {!loading && coachTeams.length >= 1 && (
          <TodayPanel coachTeams={coachTeams} />
        )}

        {/* Body */}
        {loading ? (
          <div className="space-y-3">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : allTeams.length === 0 ? (
          // Coaches with zero teams already see the getting-started
          // checklist above — stacking the EmptyState's create-team card
          // under it made THREE create-team prompts on one screen (live
          // finding, 2026-07-22). Parents (who get no checklist) keep the
          // EmptyState for its "ask your coach for an invite" guidance.
          (isParent && coachTeams.length === 0)
            ? <EmptyState firstName={firstName} />
            : null
        ) : (
          <>
            <ul className="space-y-2 sm:space-y-3">
              {allTeams.map((team) => (
                <li key={team.id}>
                  <TeamCard team={team} />
                </li>
              ))}
            </ul>

            {/* Always-available footer action — coaches can spin up
                another team without bouncing through Settings. The
                /teams/create route guard handles permission. */}
            <div className="mt-6 flex justify-center">
              <Link
                to="/teams/create"
                className="inline-flex items-center gap-2 rounded-xl border-2 border-dashed border-zinc-300 bg-white hover:border-[#b5f03d] hover:bg-[#b5f03d]/5 px-5 py-3 text-sm font-bold uppercase text-zinc-600 hover:text-[#0d1f14] transition-colors"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                <PlusIcon className="h-4 w-4" />
                {t('home.createAnother')}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
